import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { BdeseModalitesPanel } from '../components/BdeseModalitesPanel';
import { bdeseService } from '../services/bdese.service';
import {
  LIBELLE_CATEGORIE,
  LIBELLE_STATUT,
  type RapportResume,
} from '../types';

/**
 * Les exercices de la BDESE d'un dossier — le point d'entrée du module.
 *
 * LA FORME VIENT DU CHANTIER C4, elle n'est pas réinventée ici : un exercice
 * par ligne fine, toute la ligne cliquable, aucune carte d'indicateurs en
 * en-tête. Elle a passé le contrôle d'ergonomie en large et en étroit
 * (`docs/chantier-c4/carte-des-surfaces.md`). Ce fichier la porte dans le
 * produit — le chantier C4 ne le pouvait pas, le front vit dans un autre dépôt.
 *
 * CE QUE L'ÉCRAN NE FAIT PAS. Il ne déduit rien. La catégorie de seuil, le
 * référentiel applicable et la complétude sont des réponses du serveur.
 * Recalculer un seuil ici, ce serait décider depuis le navigateur de l'étendue
 * d'une obligation légale.
 */
export function BdeseExercicesPage() {
  const { folderUuid = '' } = useParams();
  const [exercices, setExercices] = useState<RapportResume[]>([]);
  const [chargement, setChargement] = useState(true);
  const [autorise, setAutorise] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [modifiable, setModifiable] = useState<boolean | null>(null);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  const recharger = useCallback(async () => {
    if (!folderUuid) return;
    setChargement(true);
    setErreurChargement(null);
    try {
      setExercices(await bdeseService.exercices(folderUuid));
      setAutorise(true);
    } catch (e: unknown) {
      const statut = (e as { response?: { status?: number } })?.response
        ?.status;
      setAutorise(statut !== 401 && statut !== 403);
      setExercices([]);
      if (statut !== 401 && statut !== 403) {
        setErreurChargement(
          'Les exercices ne peuvent pas être chargés pour le moment.'
        );
      }
    } finally {
      setChargement(false);
    }
  }, [folderUuid]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  const anneeSuivante = new Date().getFullYear();
  const dejaOuvert = exercices.some(e => e.annee === anneeSuivante);

  const ouvrir = async () => {
    setEnCours(true);
    setErreurAction(null);
    try {
      await bdeseService.ouvrirExercice(folderUuid, anneeSuivante);
      await recharger();
    } catch (e: unknown) {
      const corps = (e as { response?: { data?: { message?: string } } })
        ?.response?.data;
      setErreurAction(corps?.message ?? "L'exercice n'a pas pu être ouvert.");
    } finally {
      setEnCours(false);
    }
  };

  if (!autorise) {
    return (
      <p className="py-8 text-sm text-neutral-500">
        La base économique et sociale de cette entreprise ne vous est pas
        ouverte.
      </p>
    );
  }

  const reference = exercices[0];

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-2xl font-semibold text-neutral-900">
          Base économique et sociale
        </h1>
        {modifiable === true &&
          !chargement &&
          !erreurChargement &&
          !dejaOuvert && (
            <button
              type="button"
              disabled={enCours}
              onClick={() => void ouvrir()}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-40"
              style={{
                backgroundColor: 'var(--marque-accent)',
                color: 'var(--marque-sur-accent)',
              }}
            >
              <Plus className="h-4 w-4" />
              Ouvrir l&apos;exercice {anneeSuivante}
            </button>
          )}
      </div>

      {erreurAction && (
        <p
          className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-800"
          role="status"
        >
          {erreurAction}
        </p>
      )}

      <div className="shadow-soft overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {chargement ? (
          <p className="p-6 text-sm text-neutral-500">Chargement…</p>
        ) : erreurChargement ? (
          <div className="p-6" role="alert">
            <p className="text-sm text-red-800">{erreurChargement}</p>
            <button
              type="button"
              onClick={() => void recharger()}
              className="focus-visible:ring-primary-600 mt-3 min-h-11 rounded-lg px-3 text-sm font-medium text-neutral-700 underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950 focus-visible:ring-2 focus-visible:outline-none"
            >
              Réessayer
            </button>
          </div>
        ) : exercices.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500">
            {modifiable === true
              ? "Aucun exercice ouvert. Le bouton ci-dessus ouvre celui de l'année en cours ; les données de paie s'y déversent ensuite."
              : "Aucun exercice n'est disponible pour le moment."}
          </p>
        ) : (
          <ul>
            {exercices.map(e => (
              <li
                key={e.uuid}
                className="relative border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50"
              >
                {/* Toute la ligne est cliquable, par un VRAI lien : le clic
                    droit et l'ouverture dans un nouvel onglet restent
                    possibles, ce qu'un `onClick` sur la ligne retirerait. */}
                <Link
                  to={`/bdese/${folderUuid}/exercice/${e.uuid}`}
                  className="flex items-center justify-between gap-4 px-5 py-3.5"
                >
                  <span className="text-sm font-medium text-neutral-900">
                    Exercice {e.annee}
                  </span>
                  <span className="text-sm text-neutral-500">
                    {LIBELLE_STATUT[e.statut] ?? e.statut}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <BdeseModalitesPanel
        folderUuid={folderUuid}
        onModifiableChange={setModifiable}
      />

      {reference && (
        <details className="mt-4 max-w-3xl text-xs text-neutral-500">
          <summary className="inline-flex min-h-9 cursor-pointer items-center py-1 font-medium hover:text-neutral-800">
            Cadre juridique de cette base
          </summary>
          <div className="mt-1 space-y-2 border-l-2 border-neutral-200 pl-3 leading-relaxed">
            <p>
              Entreprise de {LIBELLE_CATEGORIE[reference.categorieSeuil]} ·
              référentiel {reference.referentielCode}, version{' '}
              {reference.referentielVersion}.
            </p>
            <p>
              Une base couvre six exercices à la fois : l&apos;année en cours,
              les deux précédentes et les trois suivantes, sous forme de grandes
              tendances. Ouvrir un exercice ne referme pas les autres.
            </p>
          </div>
        </details>
      )}
    </div>
  );
}
